import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Badge, DataTable, type TanstackReactTable } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import type { ModerationHistoryItem } from "../api/client";

const ACTION_LABELS: Record<string, string> = {
  hidden: "ເຊື່ອງ",
  removed: "ລຶບ",
  restored: "ເປີດຄືນ",
  actioned: "ດຳເນີນການ",
  dismissed: "ປິດລາຍງານ",
};

const TARGET_TYPE_LABELS: Record<string, string> = {
  listing: "ລາຍການສິນຄ້າ",
  review: "ຄຳແນະນຳ",
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງແປງ",
  job: "ວຽກ",
};

function actionVariant(
  action: string,
): "success" | "secondary" | "destructive" | "default" | "outline" {
  switch (action) {
    case "actioned":
      return "success";
    case "dismissed":
      return "secondary";
    case "hidden":
    case "removed":
      return "outline";
    case "restored":
      return "default";
    default:
      return "outline";
  }
}

type ModerationHistoryTableProps = {
  isLoading: boolean;
  data: ModerationHistoryItem[];
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function ModerationHistoryTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  onPaginationChange,
}: ModerationHistoryTableProps) {
  const columns: TanstackReactTable.ColumnDef<ModerationHistoryItem>[] = [
    {
      id: "date",
      header: "ວັນທີ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTimeLocal(row.original.date)}
        </span>
      ),
      size: 160,
    },
    {
      id: "action",
      header: "ການກະທຳ",
      cell: ({ row }) => (
        <Badge variant={actionVariant(row.original.action)}>
          {ACTION_LABELS[row.original.action] ?? row.original.action}
        </Badge>
      ),
      size: 120,
    },
    {
      id: "target",
      header: "ເປົ້າໝາຍ",
      cell: ({ row }) => {
        const r = row.original;
        const label = `${TARGET_TYPE_LABELS[r.targetType] ?? r.targetType}: ${r.targetDisplay}`;
        if (r.type === "listing") {
          return (
            <Link
              to="/app/moderation/listings/$id"
              params={{ id: r.targetId }}
              className="text-primary hover:underline"
            >
              {label}
            </Link>
          );
        }
        if (r.type === "report") {
          return (
            <Link
              to="/app/moderation/reports/$id"
              params={{ id: r.id }}
              className="text-primary hover:underline"
            >
              {label}
            </Link>
          );
        }
        return <span>{label}</span>;
      },
      size: 220,
    },
    {
      id: "actor",
      header: "ຜູ້ດຳເນີນການ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.actorDisplay ?? "—"}
        </span>
      ),
      size: 140,
    },
    {
      id: "details",
      header: "ລາຍລະອຽດ",
      cell: ({ row }) => {
        const r = row.original;
        const text =
          r.type === "report"
            ? (r.actionTaken ?? r.reason ?? "—")
            : (r.details ?? "—");
        return (
          <span className="line-clamp-1 max-w-[240px] text-muted-foreground text-sm">
            {text}
          </span>
        );
      },
      size: 260,
    },
  ];

  return (
    <DataTable<ModerationHistoryItem, unknown>
      noDataMessage="ບໍ່ພົບລາຍການ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
